import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  Tabs,
  Tab,
  Grid,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  useTheme,
} from '@mui/material';
import { get, isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { setAlert } from 'src/layouts/main/MainLayoutSlice';
import { getRegionBySlug, userRegister } from 'src/pages/CareerHistory/CareerHistorySlice';
import { base_route_url, EMAIL_REGEXP } from 'src/utils';
import * as Yup from 'yup';
import { createUKPostcodeValidation } from 'src/utils/UKPostCodeValidation';
import AuthWrapper from './AuthWrapper';
import FormTextField from './FormTextField';
import TextFIeldComponent from './TextField/TextFieldComponent';

const validationSchema = Yup.object().shape({
  registeredUserType: Yup.string().oneOf(['individual', 'business']).required(),
  firstName: Yup.string()
    .required('First Name is required')
    .min(2, 'First Name must be at least 2 characters'),
  lastName: Yup.string().required('Last Name is required'),
  email: Yup.string()
    .trim()
    .required('Email is required')
    .matches(EMAIL_REGEXP, 'Email format is not valid'),

  postcode: Yup.string().when('registeredUserType', {
    is: 'individual',
    then: () => createUKPostcodeValidation().required('Postcode is required'),
    otherwise: () => Yup.string().notRequired(),
  }),

  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .max(16, 'Password must not exceed 16 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,16}$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    ),

  isUpskill: Yup.boolean(),
  isReskill: Yup.boolean().test(
    'at-least-one-selected',
    'Select at least one option (Upskill or Reskill)',
    function (value) {
      const { isUpskill } = this.parent;
      return isUpskill || value;
    }
  ),
  reasonForUpskillReskill: Yup.string(),
  isOptInMarketing: Yup.boolean(),

  businessName: Yup.string().when('registeredUserType', {
    is: 'business',
    then: (schema) => schema,
    otherwise: () => Yup.string().notRequired(),
  }),
  businessPostcode: Yup.string().when('registeredUserType', {
    is: 'business',
    then: () => createUKPostcodeValidation('Business Postcode'),
    otherwise: () => Yup.string().notRequired(),
  }),
  position: Yup.string().when('registeredUserType', {
    is: 'business',
    then: (schema) => schema,
    otherwise: () => Yup.string().notRequired(),
  }),
  sizeOfBusiness: Yup.string().when('registeredUserType', {
    is: 'business',
    then: (schema) => schema.required('Business Size is required'),
    otherwise: () => Yup.string().notRequired(),
  }),
  acceptTerms: Yup.bool().oneOf([true], 'Terms and conditions must be accepted'),
});

const SignUpRegion = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    getValues,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      registeredUserType: 'individual',
      firstName: '',
      lastName: '',
      email: '',
      postcode: '',
      password: '',
      isUpskill: false,
      isReskill: false,
      reasonForUpskillReskill: '',
      isOptInMarketing: false,
      businessName: '',
      businessPostcode: '',
      position: '',
      sizeOfBusiness: '',
      acceptTerms: false,
    },
  });

  const [tabIndex, setTabIndex] = useState(0);
  const [isPasswordHidden, setIsPasswordHidden] = useState(true);

  const dispatch = useDispatch();
  const regions = useSelector((state) => state.careerHistory);
  const [regionData, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const params = useParams();
  const { cg_name, rg_name } = useParams();
  const navigate = useNavigate();
  const isRegion = !!params?.rg_name;
  const thm = useTheme();

  const handleSignInClicked = () => {
    if (isRegion) {
      navigate(`${base_route_url}region/${params?.rg_name}/sign-in`);
    } else {
      navigate(`${base_route_url}${params?.cg_name}/sign-in`);
    }
  };
  const onSubmit = (values) => {
    setIsLoading(true);
    
    // Separate payload based on user type
    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      password: values.password,
      registeredUserType: values.registeredUserType,
      isUpskill: values.isUpskill,
      isReskill: values.isReskill,
      reasonForUpskillReskill: values.reasonForUpskillReskill,
      isOptInMarketing: values.isOptInMarketing,
      acceptTerms: values.acceptTerms
    };
    
    if (values.registeredUserType === 'individual') {
      payload.postcode = values.postcode;
    } else {
      payload.businessName = values.businessName;
      payload.businessPostcode = values.businessPostcode;
      payload.position = values.position;
      payload.sizeOfBusiness = values.sizeOfBusiness;

    }
    
    dispatch(userRegister(payload))
      .then((response) => {
        if (get(response, 'payload.data.success')) {
          dispatch(
            setAlert({
              open: true,
              isSuccess: true,
              type: 'success',
              msg: get(response, 'payload.data.message')
                ? get(response, 'payload.data.message')
                : 'User registered successfully',
            })
          );
          handleSignInClicked();
        } else {
          const errorMessage =
            get(response, 'payload.response.data.message') ||
            'Something Went Wrong, Please try again later';

          dispatch(
            setAlert({
              open: true,
              msg: errorMessage,
            })
          );
        }
      })
      .catch((error) => console.log('error----', error))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    dispatch(getRegionBySlug(rg_name));
  }, [rg_name, dispatch]);

  useEffect(() => {
    if (regions?.regions) {
      setData(regions?.regions);
      if (!isEmpty(regionData)) {
        setLoading(false);
      }
    }
  }, [regions?.regions, regionData]);

  useEffect(() => {
    setValue('registeredUserType', tabIndex === 0 ? 'individual' : 'business');
  }, [tabIndex, setValue]);

  const buttonColor = regionData?.button?.bgColor;
  const buttonFontColor = regionData?.button?.color;
  const logo = regionData?.logo || '';
  const partnerLogos = regionData?.partnerLogos || [];
  const primaryColor = regionData?.primaryColor || '';
  const fontColor = regionData?.fontColor || '';
  const secondaryColor = regionData?.secondaryColor || '';
  const bgImage = regionData?.bgImage || '';

  const upskillValues = watch('upskillReskill') || [];
   const isAndroid = useMemo(() => /Android/i.test(navigator?.userAgent || ''), []);
  return (
    <AuthWrapper title="Sign-Up">
      <Box
        sx={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          // backgroundAttachment: 'fixed',
          pb: 5,
          mt: 0,
          backgroundAttachment: isAndroid ? 'scroll' : 'fixed',
          minHeight: { xs: 'auto', md: '90.8vh' },
          paddingBlock: { xs: 2, sm: 3, md: 3 },
          overflow: 'auto',
        }}
        className="page-content-wrapper"
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            className="content"
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{
              justifyContent: { xs: 'flex-start', md: 'center' },
              minHeight: { md: '70vh' },
              py: { xs: 2, md: 4 },
            }}
          >
            <Box mt={0} display="flex" flexDirection="column" alignItems="center">
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '20px !important', sm: '26px !important', md: '32px !important' },
                  mb: 2,
                  mt: { lg: 2 },
                  color: fontColor,
                  textAlign: 'center',
                  px: 1,
                }}
              >
                <b>Create your free ThinkSkills account</b>
              </Typography>
              <Typography
                variant="h2"
                sx={{ mb: 2, fontSize: '14px !important', color: fontColor, fontWeight: 400 }}
                component="p"
              >
                Access results immediately next time you log in
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  backgroundColor: 'white',
                  py: 4,
                  borderRadius: '8px',
                  width: '100%',
                  maxWidth: {
                    xs: '100%',
                    sm: 600,
                    md: 700,
                    lg: 800,
                  },
                  boxSizing: 'border-box',
                  px: { xs: 2, sm: 3, md: 4 },
                  boxShadow: { xs: 'none', md: '0 6px 18px rgba(0,0,0,0.08)' },
                }}
              >
                {/* Tabs */}
                {/* <Tabs
                  value={tabIndex}
                  onChange={(e, newVal) => setTabIndex(newVal)}
                  sx={{ mb: 2 }}
                  variant="fullWidth"
                >
                  <Tab label="Individual" />
                  <Tab label="Business" />
                </Tabs> */}

                <Tabs
                  value={tabIndex}
                  onChange={(e, newVal) => setTabIndex(newVal)}
                  variant="fullWidth"
                  sx={{
                    width: '100%',
                    mb: 2,
                    color: buttonColor,
                    '& .MuiTab-root': {
                      color: buttonColor,
                      textTransform: 'none',
                      fontSize: '1rem',
                      backgroundColor: primaryColor,
                      '&:first-of-type': {
                        borderRadius: '8px 0 0 0px',
                      },
                      '&:last-of-type': {
                        borderRadius: '0 8px 0px 0',
                      },
                    },
                    '& .Mui-selected': {
                      color: 'black',
                      backgroundColor: buttonColor,
                      '&:first-of-type': {
                        borderRadius: '8px 0 0 0px',
                      },
                      '&:last-of-type': {
                        borderRadius: '0 8px 0px 0',
                      },
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: 'unset',
                    },
                    '.css-j3fxsj-MuiButtonBase-root-MuiTab-root:not(:last-of-type)': {
                      marginRight: '0px',
                    },
                    '.css-kqhgxw-MuiTabs-scroller': {
                      backgroundColor: primaryColor,
                      borderRadius: '8px 8px 0 0',
                      color: buttonColor,
                    },
                    '& .MuiTab-root:not(:last-of-type)': {
                      marginRight: { xs: 0, sm: 0 },
                    },
                    [thm.breakpoints.up('sm')]: {
                      '& .MuiTab-root:not(:last-of-type)': {
                        marginRight: '0px !important',
                      },
                    },
                  }}
                >
                  <Tab label="Individual" />
                  <Tab label="Business" />
                </Tabs>

                <form onSubmit={handleSubmit(onSubmit)} style={{}}>
                  <Box className="sign-up-wrapper" sx={{ width: '100%' }}>
                    <Grid container spacing={2}>
                      {/* First Name */}
                      <Grid item xs={12} sm={6}>
                        <FormTextField
                          title="First Name"
                          isRequired
                          name="firstName"
                          placeholder="Enter First Name"
                          {...register('firstName')}
                          control={control}
                          className="input-text-field"
                          fullWidth
                        />
                      </Grid>

                      {/* Last Name */}
                      <Grid item xs={12} sm={6}>
                        <FormTextField
                          title="Last Name"
                          isRequired
                          name="lastName"
                          placeholder="Enter Last Name"
                          {...register('lastName')}
                          control={control}
                          className="input-text-field"
                          fullWidth
                        />
                      </Grid>

                      {/* Email */}
                      <Grid item xs={12}>
                        <FormTextField
                          title="Email Address"
                          isRequired
                          name="email"
                          placeholder="Enter email"
                          {...register('email')}
                          control={control}
                          fullWidth
                          className="input-text-field"
                        />
                      </Grid>

                      {tabIndex === 0 ? (
                        // Individual
                        <Grid item xs={12}>
                          <FormTextField
                            title="Postcode"
                            isRequired
                            name="postcode"
                            placeholder="Enter Postcode"
                            {...register('postcode')}
                            fullWidth
                            control={control}
                            className="input-text-field postcode"
                          />
                        </Grid>
                      ) : (
                        // Business fields
                        <>
                          <Grid item xs={12} sm={12}>
                            <FormTextField
                              title="Business Name"
                              name="businessName"
                              placeholder="Enter Business Name"
                              {...register('businessName')}
                              fullWidth
                              control={control}
                              className="input-text-field"
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormTextField
                              title="Business Postcode"
                              name="businessPostcode"
                              placeholder="Enter Business Postcode"
                              {...register('businessPostcode')}
                              fullWidth
                              control={control}
                              className="input-text-field"
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormTextField
                              title="Position"
                              name="position"
                              placeholder="Enter Position"
                              fullWidth
                              {...register('position')}
                              control={control}
                              className="input-text-field"
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Typography mb={1} variant="subtitle2">
                              Size of Business *
                            </Typography>
                            <FormGroup row>
                              <Controller
                                name="sizeOfBusiness"
                                control={control}
                                render={({ field }) => (
                                  <FormControlLabel
                                    sx={{
                                      '.MuiFormControlLabel-label': { fontSize: '14px !important' },
                                    }}
                                    control={
                                      <Checkbox
                                        {...field}
                                        checked={field.value === 'small'}
                                        onChange={() => field.onChange('small')}
                                      />
                                    }
                                    label="Small"
                                  />
                                )}
                              />
                              <Controller
                                name="sizeOfBusiness"
                                control={control}
                                render={({ field }) => (
                                  <FormControlLabel
                                    sx={{
                                      '.MuiFormControlLabel-label': { fontSize: '14px !important' },
                                    }}
                                    control={
                                      <Checkbox
                                        {...field}
                                        checked={field.value === 'medium'}
                                        onChange={() => field.onChange('medium')}
                                      />
                                    }
                                    label="Medium"
                                  />
                                )}
                              />
                              <Controller
                                name="sizeOfBusiness"
                                control={control}
                                render={({ field }) => (
                                  <FormControlLabel
                                    sx={{
                                      '.MuiFormControlLabel-label': { fontSize: '14px !important' },
                                    }}
                                    control={
                                      <Checkbox
                                        {...field}
                                        checked={field.value === 'large'}
                                        onChange={() => field.onChange('large')}
                                      />
                                    }
                                    label="Large"
                                  />
                                )}
                              />
                            </FormGroup>
                            {errors.sizeOfBusiness && (
                              <FormHelperText error>
                                {errors.sizeOfBusiness?.message}
                              </FormHelperText>
                            )}
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12}>
                        <Typography mb={1} variant="subtitle2">
                          Are you here to Upskill or Reskill{tabIndex === 1 && ' your workforce'}? *
                        </Typography>
                        <FormGroup row>
                          <Controller
                            name="isUpskill"
                            control={control}
                            render={({ field }) => (
                              <FormControlLabel
                                sx={{
                                  '.MuiFormControlLabel-label': { fontSize: '14px !important' },
                                }}
                                control={<Checkbox {...field} checked={Boolean(field.value)} />}
                                label="Upskill"
                              />
                            )}
                          />
                          <Controller
                            name="isReskill"
                            control={control}
                            render={({ field }) => (
                              <FormControlLabel
                                sx={{
                                  '.MuiFormControlLabel-label': { fontSize: '14px !important' },
                                }}
                                control={<Checkbox {...field} checked={Boolean(field.value)} />}
                                label="Reskill"
                              />
                            )}
                          />
                        </FormGroup>
                        {errors.isReskill && (
                          <FormHelperText error>{errors.isReskill?.message}</FormHelperText>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <FormTextField
                          title={
                            tabIndex === 0
                              ? 'Why are you looking to Upskill/Reskill?'
                              : 'Why are you looking to Upskill/Reskill your workforce?'
                          }
                          name="reasonForUpskillReskill"
                          placeholder={
                            tabIndex === 0
                              ? 'e.g. emerging technology, poor salary..'
                              : 'e.g. emerging technology, poor salary...'
                          }
                          {...register('reasonForUpskillReskill')}
                          control={control}
                          className="input-text-field"
                          multiline
                          rows={4}
                        />
                      </Grid>

                      {/* Password */}
                      <Grid item xs={12}>
                        <Box className="field-wrapper">
                          <Typography mb={1} variant="subtitle2">
                            Password*
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <TextFIeldComponent
                              name="password"
                              placeholder="Enter password"
                              control={control}
                              {...register('password')}
                              className="input-text-field postcode"
                              type={isPasswordHidden ? 'password' : 'text'}
                            />
                            <Button
                              sx={{
                                borderRadius: '8px',
                                color: buttonFontColor || 'white',
                              }}
                              onClick={() => setIsPasswordHidden((prevValue) => !prevValue)}
                            >
                              <Typography mb={1} variant="subtitle2">
                                {!isPasswordHidden ? 'Hide' : 'Show'} Password
                              </Typography>
                            </Button>
                          </Stack>
                          <Typography my={1} variant="caption" textAlign="left">
                            Between 8-16 characters, with at least one uppercase & lowercase letter,
                            one number and one symbol
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Marketing checkbox */}
                      <Grid item xs={12}>
                        <Controller
                          name="isOptInMarketing"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              sx={{ '.MuiFormControlLabel-label': { fontSize: '14px !important' } }}
                              control={<Checkbox {...field} checked={Boolean(field.value)} />}
                              label="I would like to receive updates from ThinkSkills"
                            />
                          )}
                        />
                      </Grid>

                      {/* Terms and conditions */}
                      <Grid item xs={12}>
                        <Controller
                          name="acceptTerms"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              sx={{ '.MuiFormControlLabel-label': { fontSize: '14px !important' } }}
                              control={<Checkbox {...field} checked={Boolean(field.value)} />}
                              label={
                                <span>
                                  I've read and accept the{' '}
                                  <a
                                    href="/terms-conditions"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: buttonColor || '#1976d2',
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    Terms and Conditions
                                  </a>
                                </span>
                              }
                            />
                          )}
                        />
                        {errors.acceptTerms && (
                          <FormHelperText error>{errors.acceptTerms?.message}</FormHelperText>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <LoadingButton
                          sx={{
                            backgroundColor: (theme) =>
                              `${buttonColor || theme.palette.primary.main} !important`,
                            borderRadius: '8px',
                            color: buttonFontColor || 'white',
                            p: 2,
                            mb: 2,
                          }}
                          loading={isLoading}
                          fullWidth
                          type="submit"
                        >
                          Create Account
                        </LoadingButton>

                        <Button
                          sx={{
                            borderRadius: '8px',
                            p: 2,
                            borderColor: (theme) =>
                              `${buttonColor || theme.palette.primary.main} !important`,
                            color: buttonFontColor || 'white',
                          }}
                          fullWidth
                          variant="outlined"
                          onClick={handleSignInClicked}
                          // onClick={(e) => showCareerDetailsPopup(item.id, e)}
                        >
                          <Stack rowGap={0.2} direction="column">
                            <Typography>Sign in</Typography>
                            <Typography>(I already have an account)</Typography>
                          </Stack>
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </form>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </AuthWrapper>
  );
};

export default SignUpRegion;
